import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { CaseInsensitiveMap } from "../../../../Global/Base/DataTypes/CaseInsensitiveMap";
import { StringUtils } from "../../../../Global/Base/Utils/StringUtils";
import { IPostInitialize } from "../../../UI.ReactControlConnectors/types";
import { loadOriginalRightsDefinition } from "../../Rights/OriginalRightsDefinitionLoader";
import { IForm } from "../XGControls/IForm";
import { ControlParams, FormControlParams, IControl, IHTMLElementWithControl } from "./ControlInterfaces";

const logger = LazyLoadingSectionLogger.create(LoggingSection.dependencyInjection);

/** Předpis metody pro kontrolu, zda se má komponenta zinicializovat */
export interface IRegistratorInitializeControlPredicate {
    (el: HTMLElement): boolean;
}

/** Předpis konstruktoru pro vytváření komponent */
export interface IConstructorSignature {
    new(params: ControlParams): IControl;
}

/** Předpis tovární metody pro vytváření komponent */
export interface IFactoryMethodSignature {
    (params: FormControlParams): IControl | Promise<IControl>;
}

/**
 * Registrátor umožňuje registrování implementací pro různé Control-y a umožňuje jejich vytváření a iniciování
 * @includeToDoc
 */
export class Registrator {
    private static _controls = new CaseInsensitiveMap<IFactoryMethodSignature>();

    public static add(controlName: string, controlClass: (IFactoryMethodSignature | IConstructorSignature)): void {
        const controlFactoryFunction: IFactoryMethodSignature = Registrator._isConstructorSignature(controlClass)
            ? (params: FormControlParams) => new controlClass(params)
            : controlClass;

        Registrator._controls.set(controlName.toLowerCase(), controlFactoryFunction);
    }

    public static get(controlName: string): IFactoryMethodSignature {
        return Registrator._controls.get(controlName.toLowerCase());
    }

    /**
     * K predanym elementum na formulari vytvori a ziniciuje Controly, umoznuje definovat, ktere elementy se mají zinicializovat
     * @param form fomular, ktery se ma predat do inicializace FormControlu
     * @param initializeElementPredicate pri splneni predikatu je element zinicializován
     * @param elements elementy ke ziniciovani
     */
    public static async initializeFormControlsWithFilter(form: IForm, initializeElementPredicate: IRegistratorInitializeControlPredicate, ...elements: HTMLElement[]): Promise<void> {
        for (const e of elements) {
            await Registrator._initializeControls(form, e, initializeElementPredicate);
        }
    }

    /**
     * K predanym elementum na formulari vytvori a ziniciuje Controly
     * @param form fomular, ktery se ma predat do inicializace FormControlu
     * @param elements elementy ke ziniciovani
     */
    public static async initializeFormControls(form: IForm, ...elements: HTMLElement[]): Promise<void> {
        await Registrator.initializeFormControlsWithFilter(form, undefined, ...elements);
    }

    /**
     * K predanym elementum vytvori a ziniciuje Controly, lze pouzit pro libovolne elementy, umoznuje definovat, ktere elementy se mají zinicializovat
     * @param initializeElementPredicate pri splneni predikatu je element zinicializován
     * @param elements elementy ke ziniciovani
     */
    public static async initializeControlsWithFilter(initializeElementPredicate: IRegistratorInitializeControlPredicate, ...elements: HTMLElement[]): Promise<void> {
        for (const e of elements) {
            await Registrator._initializeControls(null, e, initializeElementPredicate);
        }
    }

    /**
     * K predanym elementum vytvori a ziniciuje Controly, lze pouzit pro libovolne elementy
     * @param elements elementy ke ziniciovani
     */
    public static async initializeControls(...elements: HTMLElement[]): Promise<void> {
        await Registrator.initializeControlsWithFilter(undefined, ...elements);
    }

    /**
     * Rozhoduje, jestli se jedná o konstruktor nebo tovární metodu
     * @param value Funkce, která je buď konstruktor nebo tovární metoda
     */
    private static _isConstructorSignature(value: IConstructorSignature | IFactoryMethodSignature): value is IConstructorSignature { // Some kind of magic happens here
        return !StringUtils.isNullOrEmpty((value as IConstructorSignature).name);
    }

    private static async _initializeControls(
        form: IForm,
        element: HTMLElement,
        initializeElementPredicate?: IRegistratorInitializeControlPredicate,
        ctrlNameAttribute: string = "tsccontrol",
    ): Promise<void> {
        const elToInits: Array<HTMLElement | IHTMLElementWithControl> = [
            ...element.querySelectorAll<HTMLElement | IHTMLElementWithControl>(`[data-${ctrlNameAttribute}]`),
            element,
        ];

        for (const elToInit of elToInits) {
            // kontrola elementů, které se mají přeskočit
            if (initializeElementPredicate != null && !initializeElementPredicate(elToInit)) {
                continue;
            }

            if (elToInit instanceof IHTMLElementWithControl) {
                continue;
            }

            const ctrlName = elToInit.dataset[ctrlNameAttribute];
            if (ctrlName) {
                const controlFactoryFunction = Registrator.get(ctrlName.toLowerCase());
                if (controlFactoryFunction) {
                    await this._initializeControl(form, elToInit, controlFactoryFunction);
                } else {
                    logger.warn("Definition for control with class %s was not found. Skipping initialization.", ctrlName.toLowerCase());
                }
            }
        }

        elToInits
            .forEach(elToInit => {
                if (elToInit instanceof IHTMLElementWithControl && elToInit.tscControl instanceof IPostInitialize)
                    elToInit.tscControl.postInitialize();
            });
    }

    /**
     * Vytvoří a vrátí instanci {@link IControl}.
     * @private
     */
    private static async _initializeControl(
        form: IForm,
        elToInit: HTMLElement,
        controlFactoryFunction: IFactoryMethodSignature,
    ): Promise<IControl> {
        // Musí se načíst tu - ještě před instanciací control. Viz jsdoc k loadOriginalRightsDefinition/OriginalRightsDefinitionLoaderFromHTML.
        const originalRightsDefinition = loadOriginalRightsDefinition(elToInit);

        // Pozn. controlInstance nemusí být potomek FormControl, ale jen Control. Pak "ownerForm" nevyužije.
        // Ale to, že ho předáme, ničemu nevadí.
        const controlParams: FormControlParams = {
            ownerForm: form,
            element: elToInit,
            originalRightsDefinition,
        };

        const controlOrPromise = controlFactoryFunction(controlParams);
        const controlInstance: IControl = (controlOrPromise instanceof Promise) ? await controlOrPromise : controlOrPromise;
        form?.addDisposable?.(controlInstance);

        extendElementWithTscControl(elToInit, controlInstance);
        return controlInstance;
    }
}

/**
 * Rozšíří HTML prvek o property `tscControl` s referencí na instanci controlu/connectoru.
 * @param element HTML element.
 * @param tscControl Instance controlu/connectoru.
 * @returns Původní HTML prvek rozšířený o `tscControl`.
 */
export const extendElementWithTscControl = (element: HTMLElement, tscControl: IControl): IHTMLElementWithControl => {
    (element as IHTMLElementWithControl).tscControl = tscControl;
    return element as IHTMLElementWithControl;
};
